import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Image as ImageIcon, Upload, Trash2, ZoomIn } from 'lucide-react';
import Button from '../ui/Button';
import { uploadFile, deleteFile, loadAssets, saveAssets } from '../../utils/storage';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../ui/Dialog';

const ImageGallery = ({ assetId, images = [], onUpdate, onBeforeUpload }) => {
  const [localImages, setLocalImages] = useState(images || []);
  const [uploading, setUploading] = useState(false);
  const [lightboxImage, setLightboxImage] = useState(null);
  const fileInputRef = useRef(null);

  // Sync localImages with images prop when it changes
  useEffect(() => {
    setLocalImages(images || []);
  }, [images]);

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Check if it's an image
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }

    // Ensure asset exists before uploading (for new assets)
    let currentAssetId = assetId;
    if (!assetId && onBeforeUpload) {
      const asset = await onBeforeUpload();
      if (!asset || !asset.id) {
        return;
      }
      currentAssetId = asset.id;
    }

    if (!currentAssetId) {
      alert('Asset must be saved before uploading images.');
      return;
    }

    setUploading(true);
    try {
      const result = await uploadFile(currentAssetId, file, 'image');
      console.log('Upload result:', result);
      const imagePath = result.path || result.file?.path;
      if (!imagePath) {
        throw new Error('Server did not return image path');
      }
      console.log('Image path from server:', imagePath);
      const newImages = [...localImages, imagePath];
      setLocalImages(newImages);
      
      // Update asset in storage
      const assets = await loadAssets();
      const asset = assets.find(a => a.id === currentAssetId);
      if (asset) {
        asset.images = newImages;
        await saveAssets(assets);
        if (onUpdate) onUpdate(newImages);
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      alert('Failed to upload image: ' + error.message);
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDeleteImage = async (imagePath) => {
    if (!window.confirm('Delete this image?')) return;

    try {
      const filename = imagePath.split('/').pop();
      await deleteFile(assetId, filename, 'image');
      
      const newImages = localImages.filter(img => img !== imagePath);
      setLocalImages(newImages);

      // Update asset in storage
      const assets = await loadAssets();
      const asset = assets.find(a => a.id === assetId);
      if (asset) {
        asset.images = newImages;
        await saveAssets(assets);
        if (onUpdate) onUpdate(newImages);
      }
    } catch (error) {
      console.error('Error deleting image:', error);
      alert('Failed to delete image: ' + error.message);
    }
  };

  return (
    <>
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <ImageIcon className="h-4 w-4 text-muted-foreground" />
            <h4 className="text-sm font-medium text-foreground">Images</h4>
          </div>
          <div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileSelect}
              className="hidden"
              id="image-upload"
            />
            <Button
              variant="outline"
              size="sm"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="flex items-center space-x-2"
            >
              {uploading ? (
                <>Uploading...</>
              ) : (
                <>
                  <Upload className="h-4 w-4" />
                  <span>Upload</span>
                </>
              )}
            </Button>
          </div>
        </div>

        {localImages.length === 0 ? (
          <div className="text-center py-12 bg-white/5 rounded-lg border border-white/10">
            <ImageIcon className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-body text-sm">No images uploaded yet</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            <AnimatePresence>
              {localImages.map((imagePath, index) => {
                // Ensure the path is correct - should start with /uploads
                let normalizedPath = imagePath.startsWith('/uploads') 
                  ? imagePath 
                  : `/uploads/images/${imagePath}`;
                
                // Use full backend URL directly to avoid proxy issues
                const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:4000';
                const fullUrl = normalizedPath.startsWith('http') 
                  ? normalizedPath 
                  : `${API_BASE}${normalizedPath}`;
                
                return (
                  <motion.div
                    key={normalizedPath || `image-${index}`}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.9 }}
                    className="relative group aspect-square bg-white/5 rounded-lg border border-white/10 overflow-hidden"
                  >
                    <img
                      src={fullUrl}
                      alt={`Asset image ${index + 1}`}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        console.error('Image failed to load:', fullUrl);
                        // Show placeholder if image fails to load
                        e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="100" height="100"%3E%3Crect fill="%23ccc" width="100" height="100"/%3E%3Ctext fill="%23999" x="50" y="50" text-anchor="middle" dominant-baseline="middle" font-family="Arial" font-size="12"%3EImage%3C/text%3E%3C/svg%3E';
                      }}
                      onLoad={() => {
                        console.log('Image loaded successfully:', fullUrl);
                      }}
                    />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all duration-200 flex items-center justify-center space-x-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:4000';
                        const lightboxUrl = normalizedPath.startsWith('http') 
                          ? normalizedPath 
                          : `${API_BASE}${normalizedPath}`;
                        setLightboxImage(lightboxUrl);
                      }}
                      className="opacity-0 group-hover:opacity-100"
                    >
                      <ZoomIn className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeleteImage(imagePath)}
                      className="opacity-0 group-hover:opacity-100 text-destructive hover:bg-destructive/10"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </motion.div>
                );
              })}
            </AnimatePresence>
          </div>
        )}
      </div>

      <Dialog open={!!lightboxImage} onOpenChange={() => setLightboxImage(null)}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>Image Preview</DialogTitle>
          </DialogHeader>
          {lightboxImage && (
            <img
              src={lightboxImage}
              alt="Full size preview"
              className="w-full h-auto rounded-lg"
              onError={(e) => {
                // If full URL fails, try proxy path
                if (lightboxImage.startsWith('http')) {
                  const proxyPath = lightboxImage.replace(/^https?:\/\/[^/]+/, '');
                  e.target.src = proxyPath;
                }
              }}
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ImageGallery;

